import { useState, useEffect } from 'react'
import { ingestionService } from '../services/ingestionService'
import { useDataFetch } from '../hooks/useDataFetch'
import { formatDateTime, getIngestionRagStatus } from '../utils/helpers'
import Tooltip from '../components/ui/Tooltip'

/**
 * Ingestion Logs - Enterprise Standard
 * High density log viewer with detailed modal analysis.
 */
export default function IngestionLogs() {
    const { data: logs, loading, error, refetch } = useDataFetch(ingestionService.getLogs)
    const [selectedLog, setSelectedLog] = useState(null)
    const [isTriggering, setIsTriggering] = useState(false)
    const [triggerSource, setTriggerSource] = useState('find_a_tender')
    const [filters, setFilters] = useState({ month: '', year: '', fromDate: '', toDate: '', maxPages: '' })
    const [activeRun, setActiveRun] = useState(null)   // the run currently shown in the panel
    const [isResuming, setIsResuming] = useState(false)
    const [isReconnecting, setIsReconnecting] = useState(true)
    const [isDeleting, setIsDeleting] = useState(false)

    const toRunState = (run) => ({
        runId: run.id,
        source: 'find_a_tender',
        status: run.status,
        batches: run.batches ?? {},
        listing_count: run.listing_count ?? null,
        run,   // full row: search/page progress, saved totals, resume info
        startedAt: run.created_at ? new Date(run.created_at).getTime() : Date.now()
    })

    // On mount: pick the oldest non-terminal run (most progress preserved) and show it.
    // Button stays disabled until this resolves so a refresh can't accidentally start a new run.
    useEffect(() => {
        ingestionService.getAllActiveRuns()
            .then(runs => {
                if (runs.length === 0) return
                // Pick the run with the most completed batches — that's the one worth resuming.
                // Falls back to the first run if all have zero completed batches.
                const progressOf = (r) => (r.batches?.completed ?? 0) + (r.batches?.ingested ?? 0)
                const best = runs.reduce((a, b) => progressOf(b) > progressOf(a) ? b : a)
                setActiveRun(toRunState(best))
                setIsTriggering(true)
            })
            .catch(() => {})
            .finally(() => setIsReconnecting(false))
    }, [])

    const handleTrigger = async () => {
        // Guard: block if any active run exists, even ones not shown in the panel
        const existing = await ingestionService.getAllActiveRuns().catch(() => [])
        if (existing.length > 0) {
            alert(`⚠️ There is already an active run in progress.\n\nResume or wait for it to finish before starting a new one.`)
            if (!activeRun) {
                setActiveRun(toRunState(existing[0]))
                setIsTriggering(true)
            }
            return
        }
        setIsTriggering(true)
        try {
            const res = await ingestionService.runIngestion(triggerSource, filters)
            if (!res.success) {
                alert(`⚠️ ${res.message}`)
                setIsTriggering(false)
                return
            }
            if (res.data?.run_id) {
                setActiveRun({ runId: res.data.run_id, source: triggerSource, status: 'discovering', batches: {}, listing_count: null, startedAt: Date.now() })
                return
            }
            alert(`✅ ${res.message}`)
            refetch()
            setIsTriggering(false)
        } catch (error) {
            alert('❌ Failed to trigger ingestion: ' + error.message)
            setIsTriggering(false)
        }
    }

    const handleResume = async () => {
        if (!activeRun?.runId || isResuming) return
        setIsResuming(true)
        try {
            await ingestionService.resumeRun(activeRun.runId)
            setActiveRun(prev => prev ? { ...prev, status: 'enriching' } : prev)
        } catch (err) {
            alert('❌ Failed to resume run: ' + err.message)
        } finally {
            setIsResuming(false)
        }
    }

    const handleDelete = async () => {
        if (!activeRun?.runId || isDeleting) return
        if (!window.confirm('Stop and delete this running ingestion run? Completed batches will be discarded.')) return
        setIsDeleting(true)
        try {
            await ingestionService.cancelRun(activeRun.runId)
            setActiveRun(null)
            setIsTriggering(false)
            refetch()
        } catch (err) {
            alert('❌ Failed to delete run: ' + err.message)
        } finally {
            setIsDeleting(false)
        }
    }

    // Poll the active run every 5 s until it reaches a terminal status.
    useEffect(() => {
        if (!activeRun?.runId) return
        let cancelled = false

        const poll = async () => {
            try {
                const run = await ingestionService.getRunStatus(activeRun.runId)
                if (cancelled) return

                if (run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled') {
                    setActiveRun(null)
                    setIsTriggering(false)
                    if (run.status === 'completed') {
                        alert(`✅ Ingestion completed — new: ${run.new_count ?? 0}, updated: ${run.updated_count ?? 0}`)
                    } else if (run.status === 'cancelled') {
                        alert('🛑 Ingestion run was cancelled.')
                    } else {
                        alert(`❌ Ingestion failed: ${run.result_error || run.discovery_error || 'unknown error'}`)
                    }
                    refetch()
                    return
                }

                setActiveRun(prev => prev?.runId === run.id
                    ? { ...prev, status: run.status, batches: run.batches, listing_count: run.listing_count ?? prev.listing_count, run }
                    : prev)
            } catch (e) { /* transient — retry next tick */ }
        }

        poll()
        const interval = setInterval(poll, 5000)
        return () => { cancelled = true; clearInterval(interval) }
    }, [activeRun?.runId])

    useEffect(() => {
        const onUnload = (e) => { if (isTriggering) { e.preventDefault(); e.returnValue = '' } }
        window.addEventListener("beforeunload", onUnload)
        return () => window.removeEventListener("beforeunload", onUnload)
    }, [isTriggering]);

    return (
        <div className="space-y-6 animate-fade-in relative z-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Ingestion Logs</h1>
                <div className="flex items-center gap-2">
                    <button onClick={refetch} className="btn-secondary shadow-sm">
                        Refresh
                    </button>
                    <div className="flex items-center gap-2 border-l border-admin-border pl-4 ml-2">
                        <select
                            value={triggerSource}
                            onChange={(e) => {
                                setTriggerSource(e.target.value)
                                setFilters({
                                    month: '',
                                    year: '',
                                    fromDate: '',
                                    toDate: '',
                                    maxPages: ''
                                })
                            }}
                            className="select-admin font-bold py-1.5 pl-3 pr-8"
                            disabled={isTriggering}
                        >
                            <option value="find_a_tender">Find a Tender (UK)</option>
                            <option value="contracts_finder">Contracts Finder (UK)</option>
                            {/* <option value="etenders_ireland">eTenders (Ireland)</option> */}
                            {/* <option value="etenders_ni">eTenders (NI)</option> */}
                            <option value="sell2wales">Sell2Wales</option>
                            <option value="pcs_scotland">PCS Scotland</option>

                            <option value="ted">Ted</option>
                        </select>
                        {/* Dynamic Filters */}
                        {triggerSource === 'sell2wales' || triggerSource === 'pcs_scotland' ? (
                            <div className="flex gap-4">
                                <input
                                    type="number"
                                    placeholder="Month (1-12)"
                                    value={filters.month}
                                    onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                                    className='input-admin'
                                />
                                <input
                                    type="number"
                                    placeholder="Year"
                                    value={filters.year}
                                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                                    className="input-admin"
                                />
                            </div>
                        ) : null}
                        {triggerSource === 'contracts_finder' ? (
                            <div className="flex gap-3">
                                <div className="flex flex-col">
                                    <label className="text-xs text-admin-muted">From Date</label>
                                    <input
                                        type="date"
                                        value={filters.fromDate}
                                        onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                                        className="input-admin"
                                    />
                                </div>

                                <div className="flex flex-col">
                                    <label className="text-xs text-admin-muted">To Date</label>
                                    <input
                                        type="date"
                                        value={filters.toDate}
                                        onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                                        className="input-admin"
                                    />
                                </div>
                            </div>
                        ) : null}
                        {triggerSource === 'ted' ? (
                            <input
                                type="date"
                                value={filters.fromDate}
                                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                                className="input-admin"
                                placeholder="Select From Date"
                            />
                        ) : null}
                        {triggerSource === 'find_a_tender' ? (
                            <input
                                type="number"
                                placeholder="Max Pages (e.g. 5)"
                                value={filters.maxPages}
                                onChange={(e) => setFilters({ ...filters, maxPages: e.target.value })}
                                className="input-admin"
                            />
                        ) : null}
                        <button
                            onClick={handleTrigger}
                            disabled={isTriggering || isReconnecting}
                            className="px-3 py-1.5 text-sm font-bold text-white bg-slate-900 rounded hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                            {isReconnecting ? 'Checking…' : activeRun ? 'Running…' : isTriggering ? 'Triggering...' : 'Manual Ingestion'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Live Run Progress Panel */}
            {activeRun && <RunProgressPanel activeRun={activeRun} onResume={handleResume} isResuming={isResuming} onDelete={handleDelete} isDeleting={isDeleting} />}

            {/* Error State */}
            {error && (
                <div className="p-3 bg-status-error-bg border border-status-error/20 text-status-error text-sm rounded">
                    {error}
                </div>
            )}

            {/* Main Content: Split View */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Left: Logs Table */}
                <div className="lg:col-span-2 card-admin flex flex-col h-full">
                    <div className="px-5 py-4 border-b border-admin-border bg-gray-50">
                        <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Pipeline History</h2>
                    </div>
                    <div className="overflow-x-auto flex-1">
                        <table className="table-admin w-full text-left text-sm whitespace-nowrap">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 font-bold text-xs uppercase tracking-wider">Source</th>
                                    <th className="px-6 py-3 font-bold text-xs uppercase tracking-wider">Last Run</th>
                                    <th className="px-6 py-3 font-bold text-xs uppercase tracking-wider text-right">New</th>
                                    <th className="px-6 py-3 font-bold text-xs uppercase tracking-wider text-right">Updated</th>
                                    <th className="px-6 py-3 font-bold text-xs uppercase tracking-wider text-right">Errors</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {loading ? (
                                    <tr><td colSpan={5} className="p-8 text-center text-admin-muted font-medium">Loading logs...</td></tr>
                                ) : !logs || logs.length === 0 ? (
                                    <tr><td colSpan={5} className="p-8 text-center text-admin-muted font-medium">No logs available.</td></tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 transition-colors group cursor-default">
                                            <td className="px-6 py-3 font-semibold text-admin-text group-hover:text-brand-navy transition-colors">
                                                <div className="flex items-center gap-2">
                                                    <Tooltip text={`${getIngestionRagStatus(log).label}: ${getIngestionRagStatus(log).description}`}>
                                                        <span className={`w-2.5 h-2.5 rounded-full ${getIngestionRagStatus(log).dotClass}`}></span>
                                                    </Tooltip>
                                                    {log.source}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-admin-muted font-mono text-xs">{formatDateTime(log.lastRun)}</td>
                                            <td className="px-6 py-3 text-right text-status-success font-bold tabular-nums">+{log.new}</td>
                                            <td className="px-6 py-3 text-right text-admin-text font-medium tabular-nums">{log.updated}</td>
                                            <td className="px-6 py-3 text-right">
                                                {log.errors > 0 ? (
                                                    <span className={`font-bold tabular-nums px-2 py-0.5 rounded ${log.errorType === 'pipeline' ? 'bg-status-error-bg text-status-error' : 'bg-status-warning-bg text-status-warning'}`}>
                                                        {log.errors}
                                                    </span>
                                                ) : <span className="text-gray-300 font-medium">–</span>}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Right: Error Details */}
                <div className="space-y-4">
                    <div className="card-admin p-5 h-full">
                        <h3 className="text-sm font-bold text-admin-heading uppercase tracking-wide mb-4 flex items-center gap-2">
                            <svg className="w-4 h-4 text-status-error" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            Recent Failures
                        </h3>
                        {!logs ? (
                            <p className="text-xs text-admin-muted italic">Checking for errors...</p>
                        ) : logs.filter(l => l.errors > 0).length === 0 ? (
                            <div className="p-6 bg-status-success-bg text-status-success text-sm rounded-lg border border-status-success/20 text-center flex flex-col items-center gap-2">
                                <svg className="w-8 h-8 text-status-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                <span className="font-semibold">All Systems Operational</span>
                                <span className="text-xs text-status-success opacity-80">No recent ingestion failures detected.</span>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {logs.filter(l => l.errors > 0).map(log => (
                                    <div key={'err-' + log.id} className={`p-4 border rounded-lg shadow-sm transition-all hover:shadow-md ${log.errorType === 'pipeline' ? 'bg-status-error-bg/50 border-status-error/20 hover:border-status-error/40' : 'bg-status-warning-bg/50 border-status-warning/20 hover:border-status-warning/40'
                                        }`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <p className={`font-bold text-sm ${log.errorType === 'pipeline' ? 'text-status-error' : 'text-status-warning'
                                                }`}>{log.source}</p>
                                            <FailureStatusBadge status={log.failureStatus} />
                                        </div>
                                        <p className={`text-xs leading-relaxed ${log.errorType === 'pipeline' ? 'text-red-800' : 'text-amber-800'
                                            } mb-3`}>{log.errorMessage || `${log.errors} records failed validation check during the last import cycle.`}</p>

                                        <div className="flex justify-between items-center pt-2 border-t border-black/5">
                                            <p className={`text-[10px] font-mono ${log.errorType === 'pipeline' ? 'text-status-error' : 'text-status-warning'
                                                }`}>{log.lastRun}</p>
                                            <button
                                                onClick={() => setSelectedLog(log)}
                                                className={`text-xs font-bold underline decoration-1 underline-offset-2 transition-colors flex items-center gap-1 ${log.errorType === 'pipeline' ? 'text-status-error decoration-red-300 hover:text-red-900' : 'text-status-warning decoration-amber-300 hover:text-amber-900'}`}
                                            >
                                                View Log
                                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Footer Help */}
                        <div className="mt-4 pt-4 border-t border-admin-border text-[10px] text-admin-muted text-center">
                            Failures are automatically retried every hour.
                        </div>
                    </div>
                </div>
            </div>

            {/* Log Details Modal */}
            {selectedLog && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-admin-border">
                        <div className="px-6 py-4 border-b border-admin-border flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-bold">Ingestion Log Details</h3>
                            <button onClick={() => setSelectedLog(null)} className="text-admin-muted hover:text-admin-text p-1 rounded-full hover:bg-gray-200 transition-all">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <div className={`p-3 rounded-lg ${getIngestionRagStatus(selectedLog).color}`}>
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        {getIngestionRagStatus(selectedLog).status === 'green' && (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        )}
                                        {getIngestionRagStatus(selectedLog).status === 'amber' && (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        )}
                                        {getIngestionRagStatus(selectedLog).status === 'red' && (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        )}
                                        {getIngestionRagStatus(selectedLog).status === 'gray' && (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        )}
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-xl font-bold">{selectedLog.source}</h4>
                                    <p className="text-sm text-admin-muted font-mono">Run ID: {selectedLog.id} • {selectedLog.lastRun}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 mb-6">
                                <div className="p-3 bg-gray-50 rounded-lg border border-admin-border text-center">
                                    <p className="text-xs font-bold text-admin-muted uppercase tracking-widest">New</p>
                                    <p className="text-xl font-bold text-status-success">+{selectedLog.new}</p>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg border border-admin-border text-center">
                                    <p className="text-xs font-bold text-admin-muted uppercase tracking-widest">Updated</p>
                                    <p className="text-xl font-bold text-admin-text">{selectedLog.updated}</p>
                                </div>
                                <div className={`p-3 rounded-lg border text-center ${selectedLog.errors > 0 ? 'bg-status-error-bg border-status-error/20' : 'bg-gray-50 border-admin-border'}`}>
                                    <p className={`text-xs font-bold uppercase tracking-widest ${selectedLog.errors > 0 ? 'text-status-error' : 'text-admin-muted'}`}>Errors</p>
                                    <p className={`text-xl font-bold ${selectedLog.errors > 0 ? 'text-status-error' : 'text-gray-400'}`}>{selectedLog.errors}</p>
                                </div>
                            </div>

                            {selectedLog.errorMessage && (
                                <div className="p-4 bg-gray-50 rounded-lg border border-admin-border mb-6">
                                    <h5 className="text-xs font-bold text-admin-text uppercase mb-2">Error Log Output</h5>
                                    <div className="font-mono text-xs text-status-error bg-white p-3 rounded border border-admin-border overflow-x-auto whitespace-pre-wrap">
                                        {`[ERROR] Ingestion pipeline failed for source: ${selectedLog.source}\n[FATAL] ${selectedLog.errorMessage}\n   at process.ingest (${selectedLog.source.toLowerCase().replace(/\s/g, '_')}_connector.js:42:15)\n   at async Task.run (pipeline_manager.js:128:9)`}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={() => setSelectedLog(null)}
                                    className="btn-primary"
                                >
                                    Close Details
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const PHASES = [
    { key: 'discovering', label: 'Discovering' },
    { key: 'enriching',   label: 'Enriching'   },
    { key: 'finalizing',  label: 'Finalizing'  },
]

const PHASE_ORDER = { discovering: 0, enriching: 1, finalizing: 2, completed: 3, failed: 3 }

function formatElapsed(seconds) {
    if (seconds < 60) return `${seconds}s`
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}m ${s}s`
}

const STALL_MS = 3 * 60 * 1000   // matches the backend's FIND_A_TENDER_DISCOVERY_STALE_MS default

function formatAgo(ms) {
    const sec = Math.max(0, Math.floor(ms / 1000))
    if (sec < 60) return `${sec}s ago`
    const min = Math.floor(sec / 60)
    return min < 60 ? `${min}m ago` : `${Math.floor(min / 60)}h ${min % 60}m ago`
}

function Stat({ label, value, tone = 'default', hint }) {
    const tones = {
        default: 'text-admin-text bg-gray-50',
        good:    'text-status-success bg-status-success-bg',
        info:    'text-blue-600 bg-blue-50',
        warn:    'text-status-warning bg-status-warning-bg',
        bad:     'text-status-error bg-status-error-bg',
        muted:   'text-gray-300 bg-gray-50',
    }
    const body = (
        <div className={`${tones[tone] || tones.default} rounded-lg p-2.5 text-center border border-black/5`}>
            <p className="text-lg font-bold tabular-nums">{value}</p>
            <p className="text-[10px] font-semibold text-admin-muted uppercase tracking-wide">{label}</p>
        </div>
    )
    return hint ? <Tooltip text={hint}>{body}</Tooltip> : body
}

function ProgressBar({ pct, color = 'bg-blue-500' }) {
    return (
        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
        </div>
    )
}

function RunProgressPanel({ activeRun, onResume, isResuming, onDelete, isDeleting }) {
    const [elapsed, setElapsed] = useState(0)
    const [now, setNow] = useState(Date.now())

    useEffect(() => {
        const origin = activeRun.startedAt || Date.now()
        setElapsed(Math.floor((Date.now() - origin) / 1000))
        const tick = setInterval(() => {
            setElapsed(Math.floor((Date.now() - origin) / 1000))
            setNow(Date.now())
        }, 1000)
        return () => clearInterval(tick)
    }, [activeRun.runId])

    const run = activeRun.run || {}
    const currentPhaseIdx = PHASE_ORDER[activeRun.status] ?? 0

    // Batches: "saved" (ingested) are finished work too — they are no longer counted as "completed".
    const b = activeRun.batches || {}
    const saved      = b.ingested   || 0
    const enriched   = b.completed  || 0
    const failed     = b.failed     || 0
    const processing = b.processing || 0
    const pending    = b.pending    || 0
    const done  = saved + enriched + failed
    const total = done + processing + pending
    const pct   = total > 0 ? Math.round((done / total) * 100) : 0

    // Discovery progress (columns exist once the backend migration has run; absent → panel degrades gracefully).
    const hasSearchInfo = (run.searches_total ?? 0) > 0
    const searchesTotal = run.searches_total ?? 0
    const searchesDone  = run.searches_completed ?? 0
    const curSearch     = run.current_search ?? 0
    const pagesTotal    = run.current_search_pages_total ?? 0
    const pagesDone     = run.current_search_pages_done ?? 0
    const searchPct     = searchesTotal > 0 ? Math.round((searchesDone / searchesTotal) * 100) : 0
    const pagePct       = pagesTotal > 0 ? Math.round((pagesDone / pagesTotal) * 100) : 0
    const isDiscovering = activeRun.status === 'discovering'

    const updatedAt = run.updated_at ? new Date(run.updated_at).getTime() : null
    const quietFor  = updatedAt ? now - updatedAt : 0
    const looksStalled = isDiscovering && updatedAt != null && quietFor > STALL_MS
    const canResumeScrape = looksStalled && run.has_checkpoint
    const resumedCount = run.resumed_count ?? 0

    const showSaved = (run.relevant_count ?? 0) + (run.new_count ?? 0) + (run.updated_count ?? 0) + (run.duplicate_count ?? 0) + (run.error_count ?? 0) > 0

    return (
        <div className="card-admin border-l-4 border-l-blue-500 overflow-hidden animate-fade-in">
            {/* Panel header */}
            <div className="px-5 py-3 bg-blue-50 border-b border-admin-border flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                    </span>
                    <span className="text-sm font-bold text-blue-900">
                        Live Run — {activeRun.source.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                    <span className="text-xs text-blue-500 font-mono ml-1">
                        {activeRun.runId}
                    </span>
                    {resumedCount > 0 && (
                        <span className="px-1.5 py-0.5 rounded border border-blue-200 bg-white text-[10px] font-bold uppercase tracking-wider text-blue-600">
                            Resumed ×{resumedCount}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-blue-600 tabular-nums">
                        {formatElapsed(elapsed)}
                    </span>
                    <button
                        onClick={onDelete}
                        disabled={isDeleting}
                        className="px-2.5 py-1 text-xs font-bold rounded border border-status-error text-status-error hover:bg-status-error-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDeleting ? 'Deleting…' : 'Delete'}
                    </button>
                </div>
            </div>

            <div className="px-5 py-4 space-y-5">
                {/* Phase stepper */}
                <div className="flex items-center gap-0">
                    {PHASES.map((phase, idx) => {
                        const isDone    = currentPhaseIdx > idx
                        const isActive  = currentPhaseIdx === idx
                        return (
                            <div key={phase.key} className="flex items-center flex-1 last:flex-none">
                                <div className="flex flex-col items-center gap-1 min-w-[72px]">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                                        isDone   ? 'bg-status-success border-status-success text-white' :
                                        isActive ? 'bg-blue-500 border-blue-500 text-white animate-pulse' :
                                                   'bg-white border-gray-200 text-gray-300'
                                    }`}>
                                        {isDone ? (
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : idx + 1}
                                    </div>
                                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                                        isDone ? 'text-status-success' : isActive ? 'text-blue-600' : 'text-gray-300'
                                    }`}>{phase.label}</span>
                                </div>
                                {idx < PHASES.length - 1 && (
                                    <div className={`flex-1 h-0.5 mb-4 mx-1 transition-all ${isDone ? 'bg-status-success' : 'bg-gray-100'}`} />
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Stalled scrape: the process behind it stopped reporting */}
                {looksStalled && (
                    <div className="rounded-lg border border-status-warning/40 bg-status-warning-bg px-3 py-2.5 flex items-center justify-between gap-4">
                        <p className="text-xs text-admin-text">
                            <span className="font-bold text-status-warning">No progress {formatAgo(quietFor)}.</span>{' '}
                            {canResumeScrape
                                ? `The scrape looks interrupted. Resume continues from search ${curSearch || 1}${pagesDone ? `, skipping the ${pagesDone} page${pagesDone > 1 ? 's' : ''} already done` : ''} — finished searches are not repeated.`
                                : 'The scrape looks interrupted and has no checkpoint to continue from (it started before resume support).'}
                        </p>
                        {canResumeScrape && (
                            <button
                                onClick={onResume}
                                disabled={isResuming}
                                className="flex-shrink-0 px-3 py-1.5 text-xs font-bold rounded border border-status-warning text-status-warning bg-white hover:bg-status-warning-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResuming ? 'Resuming…' : 'Resume from checkpoint'}
                            </button>
                        )}
                    </div>
                )}

                {/* 1 · Scraping */}
                <section className="space-y-2">
                    <div className="flex items-center justify-between">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-admin-muted flex items-center gap-2">
                            {isDiscovering && !looksStalled && (
                                <svg className="animate-spin h-3.5 w-3.5 text-blue-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                            )}
                            1 · Scraping search results
                        </h4>
                        {updatedAt != null && isDiscovering && (
                            <span className="text-[11px] text-admin-muted">last activity {formatAgo(quietFor)}</span>
                        )}
                    </div>

                    {hasSearchInfo ? (
                        <>
                            <div className="flex justify-between items-center text-xs text-admin-muted font-medium">
                                <span>Searches completed</span>
                                <span className="tabular-nums font-bold text-admin-text">{searchesDone} / {searchesTotal}</span>
                            </div>
                            <ProgressBar pct={searchPct} color="bg-blue-500" />

                            {isDiscovering && curSearch > 0 && pagesTotal > 0 && (
                                <>
                                    <div className="flex justify-between items-center text-xs text-admin-muted font-medium pt-1">
                                        <span>Current search {curSearch} — pages</span>
                                        <span className="tabular-nums font-bold text-admin-text">{pagesDone} / {pagesTotal}</span>
                                    </div>
                                    <ProgressBar pct={pagePct} color="bg-blue-300" />
                                </>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                                <Stat label="Listings found" value={run.listing_count ?? 0} tone="info" hint="Unique notices found by the searches so far" />
                                <Stat label="Pages scraped" value={run.pages_scraped ?? 0} hint="Result pages fetched and read (all searches)" />
                                <Stat label="Pages skipped" value={run.pages_skipped ?? 0} tone={(run.pages_skipped ?? 0) > 0 ? 'good' : 'muted'} hint="Pages already finished before a resume — not scraped again" />
                                <Stat label="Pages failed" value={run.pages_failed ?? 0} tone={(run.pages_failed ?? 0) > 0 ? 'bad' : 'muted'} hint="Pages that still failed after retries; they are retried if the run is resumed" />
                            </div>
                        </>
                    ) : (
                        <p className="text-xs text-admin-muted">
                            {isDiscovering
                                ? 'Scraping listing pages…'
                                : 'Scraping finished.'}
                            {activeRun.listing_count != null && (
                                <> <span className="font-bold text-admin-text">{activeRun.listing_count}</span> listings discovered.</>
                            )}
                        </p>
                    )}
                </section>

                {/* 2 · Enrichment + saving (runs alongside scraping) */}
                {total > 0 && (
                    <section className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-admin-muted">2 · Enriching &amp; saving</h4>
                            {isDiscovering && <span className="text-[11px] text-admin-muted">runs alongside scraping</span>}
                        </div>
                        <div className="flex justify-between items-center text-xs text-admin-muted font-medium">
                            <span>Batches processed so far</span>
                            <span className="tabular-nums font-bold text-admin-text">{done} / {total}{isDiscovering ? '+' : ''}</span>
                        </div>
                        <ProgressBar pct={pct} color="bg-status-success" />

                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-1">
                            <Stat label="Saved" value={saved} tone={saved > 0 ? 'good' : 'muted'} hint="Enriched and written to the database" />
                            <Stat label="Enriched" value={enriched} tone="info" hint="AI details extracted; waiting to be saved (they are saved when the run finishes if saving failed earlier)" />
                            <Stat label="Processing" value={processing} tone="info" />
                            <Stat label="Pending" value={pending} />
                            <Stat label="Failed" value={failed} tone={failed > 0 ? 'bad' : 'muted'} hint="Retries exhausted — saved as listing-only rows when the run finishes" />
                        </div>

                        {(processing > 0 || pending > 0) && !isDiscovering && (
                            <div className="flex items-center justify-between pt-2 border-t border-admin-border mt-1">
                                <p className="text-xs text-admin-muted">
                                    {processing > 0 && <span className="text-status-warning font-semibold">{processing} batch{processing > 1 ? 'es' : ''} still processing</span>}
                                    {processing > 0 && pending > 0 && <span>, </span>}
                                    {pending > 0 && <span>{pending} pending</span>}
                                    <span className="ml-1">— stuck? Re-enqueue without losing completed work.</span>
                                </p>
                                <button
                                    onClick={onResume}
                                    disabled={isResuming}
                                    className="ml-4 flex-shrink-0 px-3 py-1.5 text-xs font-bold rounded border border-status-warning text-status-warning hover:bg-status-warning-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isResuming ? 'Resuming…' : 'Resume stuck batches'}
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* 3 · Results so far */}
                {showSaved && (
                    <section className="space-y-2">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-admin-muted">3 · Results so far</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            <Stat label="Relevant" value={run.relevant_count ?? 0} tone="info" hint="Passed the relevance check (new + updated + duplicates)" />
                            <Stat label="New" value={run.new_count ?? 0} tone={(run.new_count ?? 0) > 0 ? 'good' : 'muted'} />
                            <Stat label="Updated" value={run.updated_count ?? 0} tone={(run.updated_count ?? 0) > 0 ? 'good' : 'muted'} />
                            <Stat label="Duplicates" value={run.duplicate_count ?? 0} hint="Already in the database and unchanged" />
                            <Stat label="Errors" value={run.error_count ?? 0} tone={(run.error_count ?? 0) > 0 ? 'bad' : 'muted'} />
                        </div>
                    </section>
                )}
            </div>
        </div>
    )
}

function FailureStatusBadge({ status }) {
    const styles = {
        open: 'bg-white text-status-error border-status-error/30',
        retried: 'bg-white text-status-warning border-status-warning/30',
        resolved: 'bg-white text-status-success border-status-success/30',
    }
    const style = styles[status] || styles.open

    return (
        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${style} shadow-sm`}>
            {status}
        </span>
    )
}
